export function shopDay(now = new Date(), timeZone = 'America/Chicago') {
  const parts = new Intl.DateTimeFormat('en-CA', {timeZone, year:'numeric', month:'2-digit', day:'2-digit'}).formatToParts(now);
  const part = type => parts.find(p => p.type === type).value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function dateFromDay(day) {
  return new Date(`${day}T12:00:00Z`);
}

export function dayKey(date) {
  return date.toISOString().slice(0, 10);
}

export function addCalendarDays(day, count) {
  const date = dateFromDay(day);
  date.setUTCDate(date.getUTCDate() + count);
  return dayKey(date);
}

export function addBusinessDays(day, count) {
  let date = dateFromDay(day);
  while (count > 0) {
    date.setUTCDate(date.getUTCDate() + 1);
    if (date.getUTCDay() !== 0 && date.getUTCDay() !== 6) count -= 1;
  }
  return dayKey(date);
}

export function productionSchedule(now = new Date(), timeZone = 'America/Chicago') {
  const today = shopDay(now, timeZone);
  let proofStart = today;
  while ([0, 6].includes(dateFromDay(proofStart).getUTCDay())) proofStart = addCalendarDays(proofStart, 1);
  const proofDue = addBusinessDays(proofStart, 1);
  const processingComplete = addBusinessDays(proofDue, 1);
  return {today, proofDue, processingComplete, rush:addBusinessDays(proofDue, 2), standardStart:addBusinessDays(processingComplete, 3), standardEnd:addBusinessDays(processingComplete, 4)};
}

export function scheduleMonths(schedule) {
  return [...new Set([schedule.today, schedule.rush, schedule.standardStart, schedule.standardEnd].map(day => day.slice(0, 7)))].sort();
}

export function monthCells(month) {
  const first = dateFromDay(`${month}-01`);
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0, 12));
  const cells = Array(first.getUTCDay()).fill(null);
  for (let day = 1; day <= last.getUTCDate(); day += 1) cells.push(`${month}-${String(day).padStart(2, '0')}`);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const ElementBase = globalThis.HTMLElement || class {};

class HeartcraftedProductionCalendar extends ElementBase {
  connectedCallback() {
    this.lastMinute = '';
    this.update();
    this.timer = setInterval(() => this.update(), 60000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  update() {
    const now = new Date();
    const schedule = productionSchedule(now, this.dataset.timeZone);
    const signature = JSON.stringify(schedule);
    if (signature === this.lastMinute) return;
    this.lastMinute = signature;
    const locale = this.dataset.locale || 'en-US';
    const short = new Intl.DateTimeFormat(locale, {month:'short', day:'numeric', weekday:'short', timeZone:'UTC'});
    const full = new Intl.DateTimeFormat(locale, {month:'long', day:'numeric', year:'numeric', timeZone:'UTC'});
    const monthFormat = new Intl.DateTimeFormat(locale, {month:'long', year:'numeric', timeZone:'UTC'});
    this.querySelector('[data-proof-date]').textContent = short.format(dateFromDay(schedule.proofDue));
    this.querySelector('[data-processing-date]').textContent = short.format(dateFromDay(schedule.processingComplete));
    this.querySelector('[data-rush-date]').textContent = short.format(dateFromDay(schedule.rush));
    this.querySelector('[data-standard-date]').textContent = `${short.format(dateFromDay(schedule.standardStart))} – ${short.format(dateFromDay(schedule.standardEnd))}`;
    const host = this.querySelector('[data-calendar-months]');
    host.replaceChildren();
    for (const month of scheduleMonths(schedule)) {
      const table = document.createElement('table');
      table.className = 'hc-pcal__month';
      table.setAttribute('aria-label', `${this.dataset.calendarLabel}: ${monthFormat.format(dateFromDay(`${month}-01`))}`);
      const caption = table.createCaption();
      caption.textContent = monthFormat.format(dateFromDay(`${month}-01`));
      const head = table.createTHead().insertRow();
      const weekdays = new Intl.DateTimeFormat(locale, {weekday:'short', timeZone:'UTC'});
      for (let offset = 0; offset < 7; offset += 1) {
        const th = document.createElement('th');
        th.scope = 'col';
        th.textContent = weekdays.format(dateFromDay(addCalendarDays('2023-01-01', offset)));
        head.append(th);
      }
      const body = table.createTBody();
      const cells = monthCells(month);
      for (let index = 0; index < cells.length; index += 7) {
        const row = body.insertRow();
        for (const day of cells.slice(index, index + 7)) {
          const cell = row.insertCell();
          if (!day) continue;
          const mark = document.createElement('span');
          mark.className = 'hc-pcal__day';
          mark.textContent = String(Number(day.slice(-2)));
          const labels = [full.format(dateFromDay(day))];
          if (day === schedule.today) {
            mark.classList.add('hc-pcal__day--today');
            mark.setAttribute('aria-current', 'date');
            labels.push(this.dataset.todayLabel);
          }
          if (day === schedule.rush) {
            mark.classList.add('hc-pcal__day--rush');
            labels.push(this.dataset.rushLabel);
          }
          if (day === schedule.standardStart || day === schedule.standardEnd) {
            mark.classList.add('hc-pcal__day--standard');
            labels.push(this.dataset.standardLabel);
          }
          mark.setAttribute('aria-label', labels.join(', '));
          cell.append(mark);
        }
      }
      host.append(table);
    }
  }
}

if (globalThis.customElements && !customElements.get('hc-production-calendar')) {
  customElements.define('hc-production-calendar', HeartcraftedProductionCalendar);
}
